/**
 * Decisión de App Check (ADR-SEC-014 local): ¿se inicializa, con qué clave, o se lanza?
 *
 * Es una función pura a propósito. `src/services/firebase/config.ts` corre al
 * importarse y termina creando los servicios, así que lo que decide ahí sólo se
 * puede probar stubbeando medio SDK. La decisión vive aquí, con su suite; el I/O
 * (`initializeAppCheck`) se queda en `config.ts`.
 *
 * Reglas:
 * - Con los emuladores NO se inicializa: no exigen App Check y `localhost` no está en
 *   ninguna llave.
 * - Fuera de los emuladores, sin clave (ausente, vacía o sólo espacios) se LANZA:
 *   la app no monta y la consola nombra la variable. Nunca se inicializa App Check
 *   con una clave vacía en silencio.
 *
 * Receta del piloto `quizoteca-admin` (ADR-SEC-023), copiada con el mismo nombre de
 * variable, la misma condición y la misma prueba.
 */

/** Nombre de la variable de entorno (specs/COHERENCIA.md §3). */
export const APP_CHECK_SITE_KEY_ENV = 'VITE_APPCHECK_RECAPTCHA_SITE_KEY'

export type AppCheckDecision =
  | { readonly kind: 'skip'; readonly reason: 'emulators' }
  | { readonly kind: 'init'; readonly siteKey: string }

export interface AppCheckDecisionInput {
  /**
   * Verdadero cuando el arranque habla con los emuladores. Debe ser LA MISMA
   * condición que gobierna las conexiones a los emuladores en `config.ts`.
   */
  readonly usesEmulators: boolean
  /** Valor crudo de `VITE_APPCHECK_RECAPTCHA_SITE_KEY` tal como lo entrega Vite. */
  readonly siteKey: string | undefined
}

export class MissingAppCheckSiteKeyError extends Error {
  constructor() {
    super(
      `[App Check] Falta ${APP_CHECK_SITE_KEY_ENV} en el .env.<modo> con el que se construyó ` +
        'este bundle. Fuera de los emuladores el portal no arranca sin atestación (ADR-SEC-014); ' +
        'el valor acordado de cada entorno está en specs/COHERENCIA.md §3.'
    )
    this.name = 'MissingAppCheckSiteKeyError'
  }
}

export function decideAppCheck(input: AppCheckDecisionInput): AppCheckDecision {
  if (input.usesEmulators) {
    return { kind: 'skip', reason: 'emulators' }
  }
  const siteKey = input.siteKey?.trim() ?? ''
  if (siteKey === '') {
    throw new MissingAppCheckSiteKeyError()
  }
  return { kind: 'init', siteKey }
}
